import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { getIndividualMenu } from '../data/menuData';
import { useProfile } from '../context/userContext';
import { useAuth0 } from '@auth0/auth0-react';
import { useOrganization } from '../context/OrganizationContext';
import { ExpandMore, ExpandLess, AccountCircle, Logout, Person, ChevronLeft, ChevronRight, Login } from '@mui/icons-material';
import { IconButton, Tooltip, Divider, Button } from '@mui/material';
import OrganizationSelector from './organization/OrganizationSelector';

/**
 * Individual User Sidebar Component
 * Features:
 * - Minimal 7-item menu focused on LAANC operations
 * - NO fleet management (maxDrones=0)
 * - Expandable submenus for Compliance and Settings
 * - Collapsible sidebar with toggle button
 * - Upgrade banner to convert to organization
 * - WCAG 2.1 AA compliant with keyboard navigation
 */
function Sidebar({ collapsed = false, onCollapsedChange, onNavigate, isInDrawer = false }) {
  const { profile } = useProfile();
  const location = useLocation();
  const { logout, loginWithRedirect } = useAuth0();
  const { isOrganizationUser, hasIndividualSubscription } = useOrganization();
  const [expandedSections, setExpandedSections] = useState({});

  const handleLogout = () => {
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  };

  const handleLogin = () => {
    loginWithRedirect();
  };

  // Get individual menu items
  const menuItems = getIndividualMenu(profile, { isOrganizationUser, hasIndividualSubscription });

  const handleSectionToggle = (itemId) => {
    setExpandedSections((prev) => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const isLinkActive = (path) => {
    return location.pathname === path;
  };

  const isParentActive = (item) => {
    if (!item.submenu) return false;
    return item.submenu.some(sub => {
      // Strip query string from sub path for pathname comparison
      const subPathname = sub.path.split('?')[0];
      return location.pathname === subPathname;
    });
  };

  const handleKeyDown = (event, item) => {
    // Keyboard navigation support (WCAG 2.1)
    // Use Tab/Shift+Tab to navigate between menu items
    // Use Enter/Space to activate items or toggle submenus
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (item.hasSubmenu) {
        handleSectionToggle(item.id);
      }
    }
  };

  const renderDivider = (index) => (
    <div key={`divider-${index}`} className="my-3 mx-6 border-t border-blue-600" role="separator" aria-hidden="true" />
  );

  const renderUpgradeBanner = (item, index) => {
    // Don't show upgrade banner when collapsed
    if (collapsed) {
      return null;
    }

    return (
      <NavLink
        key={`upgrade-${index}`}
        to={item.path}
        onClick={() => onNavigate?.()}
        className="mx-3 mb-3 p-4 bg-blue-900 hover:bg-blue-800 border border-blue-700 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 block group"
        aria-label={item.ariaLabel}
      >
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0 w-10 h-10 bg-white rounded-lg flex items-center justify-center text-blue-600 shadow-sm group-hover:shadow-md transition-shadow duration-300">
            {item.icon}
          </div>
          <div className="flex-1 min-w-0">
            <span className="block text-sm font-semibold text-white truncate">{item.title}</span>
            <span className="block text-xs text-blue-300 truncate">{item.subtitle}</span>
          </div>
        </div>
      </NavLink>
    );
  };

  const renderLoginButton = (item, index) => {
    if (collapsed) {
      return (
        <li key={`login-${index}`} role="none">
          <Tooltip title={item.title} placement="right">
            <button
              onClick={handleLogin}
              className="flex items-center justify-center w-full h-12 transition-all duration-300 group text-white hover:bg-blue-600 hover:text-white hover:shadow-md"
              aria-label={item.ariaLabel}
              role="menuitem"
            >
              <Login className="text-lg flex-shrink-0" />
            </button>
          </Tooltip>
        </li>
      );
    }

    return (
      <li key={`login-${index}`} role="none">
        <div className="p-4 mb-2">
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleLogin}
            startIcon={<Login />}
            className="bg-blue-700 hover:bg-blue-800 text-white font-medium py-3 px-4 shadow-md hover:shadow-lg transition-all duration-300"
            aria-label={item.ariaLabel}
          >
            {item.title}
          </Button>
        </div>
      </li>
    );
  };

  const handleItemClick = (e, item) => {
    // If sidebar is collapsed, expand it
    if (collapsed) {
      onCollapsedChange?.(false);
      // If it has submenu, prevent navigation and expand the section
      if (item.hasSubmenu) {
        e.preventDefault();
        setExpandedSections((prev) => ({
          ...prev,
          [item.id]: true
        }));
      }
      // For regular items, allow navigation to proceed
    } else {
      // Call onNavigate when sidebar is expanded and actually navigating
      onNavigate?.();
    }
  };

  const renderMenuItem = (item, index) => {
    const isExpanded = expandedSections[item.id];
    const isActive = isLinkActive(item.path);
    const hasActiveChild = isParentActive(item);

    if (item.hasSubmenu) {
      // When collapsed, show as clickable item that expands sidebar and submenu
      if (collapsed) {
        return (
          <li key={item.id} role="none">
            <Tooltip title={item.title} placement="right">
              <div
                onClick={(e) => handleItemClick(e, item)}
                className={`relative flex items-center justify-center w-full h-12 transition-all duration-300 group cursor-pointer ${hasActiveChild || isActive
                    ? 'bg-white text-blue-600 shadow-lg border-l-4 border-blue-600'
                    : 'text-white hover:bg-blue-600 hover:text-white hover:shadow-md'
                  }`}
                aria-label={item.ariaLabel}
                aria-current={isActive ? 'page' : undefined}
                role="menuitem"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleItemClick(e, item);
                  }
                }}
              >
                <span className="text-lg flex items-center justify-center">
                  {hasActiveChild || isActive ? (item.iconFill || item.icon) : item.icon}
                </span>
              </div>
            </Tooltip>
          </li>
        );
      }

      return (
        <li key={item.id} className="mb-2">
          <div
            className={`relative flex items-center justify-between p-4 transition-all duration-300 group ${hasActiveChild
                ? 'bg-white text-blue-600 shadow-lg border-l-4 border-blue-600'
                : 'hover:bg-blue-600 hover:text-white hover:shadow-md'
              }`}
            onClick={() => handleSectionToggle(item.id)}
            onKeyDown={(e) => handleKeyDown(e, item)}
            role="button"
            tabIndex={0}
            aria-expanded={isExpanded}
            aria-label={item.ariaLabel}
            aria-controls={`submenu-${item.id}`}
          >
            <div className="flex items-center space-x-4">
              <span className={`text-lg flex items-center justify-center w-6 h-6 ${hasActiveChild ? 'text-blue-600' : 'text-white group-hover:text-white'
                } transition-colors duration-300`}>
                {hasActiveChild ? (item.iconFill || item.icon) : item.icon}
              </span>
              <span className={`font-medium ${hasActiveChild ? 'text-blue-600' : 'text-white group-hover:text-white'
                } transition-colors duration-300`}>{item.title}</span>
            </div>
            <span className={`text-lg ${hasActiveChild ? 'text-blue-600' : 'text-white group-hover:text-white'
              } transition-all duration-300`}>
              {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </span>
          </div>

          {/* Submenu */}
          <ul
            id={`submenu-${item.id}`}
            className={`ml-3 mr-3 mt-1 space-y-1 overflow-hidden transition-all duration-300 ${isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
              }`}
            role="menu"
            aria-label={`${item.title} submenu`}
          >
            {item.submenu.map((subItem) => {
              const subActive = isLinkActive(subItem.path);
              return (
                <li key={subItem.id} role="none">
                  <NavLink
                    to={subItem.path}
                    onClick={() => onNavigate?.()}
                    className={`flex items-center p-3 ml-8 transition-all duration-300 ${subActive
                        ? 'text-white bg-blue-600 shadow-sm'
                        : 'text-blue-300 hover:bg-blue-600 hover:text-white hover:shadow-sm'
                      }`}
                    aria-label={subItem.ariaLabel}

                    role="menuitem"
                  >
                    <span className="text-sm">{subItem.title}</span>
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </li>
      );
    }

    // Regular menu item (with or without text based on collapsed state)
    return (
      <li key={item.id} role="none">
        <Tooltip title={collapsed ? item.title : ''} placement="right">
          <NavLink
            to={item.path}
            onClick={(e) => handleItemClick(e, item)}
            className={`relative flex items-center transition-all duration-300 group ${isActive
                ? 'bg-white text-blue-600 shadow-lg border-l-4 border-blue-600'
                : 'text-white hover:bg-blue-600 hover:text-white hover:shadow-md'
              } ${collapsed
                ? 'w-full h-12 justify-center'
                : 'p-4 space-x-4'
              }`}
            aria-label={item.ariaLabel}
            aria-current={isActive ? 'page' : undefined}
            role="menuitem"
          >
            <span className={`text-lg flex items-center justify-center flex-shrink-0 ${collapsed ? '' : 'w-6 h-6'
              }`}>
              {isActive ? (item.iconFill || item.icon) : item.icon}
            </span>
            {!collapsed && (
              <span className="font-medium truncate transition-colors duration-300">{item.title}</span>
            )}
          </NavLink>
        </Tooltip>
      </li>
    );
  };

  return (
    <nav className={`flex flex-col bg-blue-900 border-r border-blue-700 shadow-lg transition-all duration-300 ${isInDrawer ? '' : 'fixed left-0 top-14 z-40'
      } ${collapsed ? 'w-16' : 'w-64'
      } ${isInDrawer ? 'h-full' : 'h-[calc(100vh-3.5rem)]'
      } overflow-y-auto`} aria-label="Main navigation">
      {/* Collapse/Expand Button - Very Top */}
      <div className={`flex p-1 mt-2 bg-blue-900 ${collapsed ? 'justify-center' : 'justify-end'
        }`}>
        <Tooltip title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} placement="right">
          <IconButton
            onClick={() => onCollapsedChange?.(!collapsed)}
            className="p-2 text-white hover:text-blue-300 hover:bg-blue-800 transition-all duration-300 rounded-lg shadow-sm hover:shadow-md"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            size="small"
            sx={{ color: 'white' }}
          >
            {collapsed ? <ChevronRight /> : <ChevronLeft />}
          </IconButton>
        </Tooltip>
      </div>

      {/* Header with user/org info */}
      <div className={`bg-blue-900 ${collapsed ? 'p-0' : 'p-4'
        }`}>
        {/* Organization Selector - show if user has organizations */}
        {isOrganizationUser && !collapsed && (
          <div className="mb-3">
            <OrganizationSelector collapsed={false} />
          </div>
        )}

        {/* Organization Selector collapsed state */}
        {isOrganizationUser && collapsed && (
          <div className="flex justify-center">
            <OrganizationSelector collapsed={true} />
          </div>
        )}

        {/* User info - only show for logged in users when expanded (and no orgs) */}
        {profile && !collapsed && !isOrganizationUser && (
          <div className="flex items-center space-x-4 p-4 bg-blue-800 shadow-sm border border-blue-700">
            <div className="flex-shrink-0 text-white">
              <AccountCircle fontSize="large" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-sm font-semibold text-white truncate">
                {profile.firstName} {profile.lastName}
              </h2>
              <p className="text-xs text-blue-300 truncate">
                {profile.subscription?.name || 'Free Plan'}
              </p>
            </div>
          </div>
        )}

        {/* User avatar only when collapsed (and no orgs) */}
        {profile && collapsed && !isOrganizationUser && (
          <div className="flex justify-center">
            <div className="w-10 h-10 bg-blue-700 flex items-center justify-center text-white shadow-md">
              <AccountCircle fontSize="medium" />
            </div>
          </div>
        )}
      </div>

      {/* Menu items */}
      <ul className="flex-1 py-4 space-y-2" role="menu">
        {menuItems.map((item, index) => {
          if (item.type === 'divider') {
            return renderDivider(index);
          }
          if (item.type === 'upgrade-banner') {
            return renderUpgradeBanner(item, index);
          }
          if (item.type === 'login-button') {
            return renderLoginButton(item, index);
          }
          return renderMenuItem(item, index);
        })}
      </ul>

      {/* Account footer - show for logged in users (both collapsed and expanded) */}
      {profile && (
        <div className="border-t border-blue-700 bg-gradient-to-r from-blue-900 to-blue-800 p-3">
          <Divider className="mb-3" />
          <div className="flex items-center justify-around space-x-2">
            <Tooltip title="View Profile" placement={collapsed ? "right" : "top"}>
              <NavLink
                to="/profile"
                onClick={() => onNavigate?.()}
                className="flex items-center justify-center p-2 text-white hover:text-blue-300 hover:bg-blue-800 rounded-lg transition-all duration-300 flex-1"
              >
                <Person fontSize="small" />
                {!collapsed && <span className="ml-2 text-sm font-medium">Profile</span>}
              </NavLink>
            </Tooltip>
            <Tooltip title="Logout" placement={collapsed ? "right" : "top"}>
              <button
                onClick={handleLogout}
                className="flex items-center justify-center p-2 text-white hover:text-red-300 hover:bg-blue-800 rounded-lg transition-all duration-300 flex-1"
              >
                <Logout fontSize="small" />
                {!collapsed && <span className="ml-2 text-sm font-medium">Logout</span>}
              </button>
            </Tooltip>
          </div>
        </div>
      )}
    </nav>
  );
}

export default Sidebar;
